/**
 * Global AI Chat Page
 * Organization-wide chat using global_chat_agent
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Stack,
  useTheme,
  Chip,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  InputAdornment,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  Tooltip
} from '@mui/material';
import {
  PersonOutlined as PersonIcon,
  AddOutlined as AddIcon,
  CloseOutlined as CloseIcon,
  SearchOutlined as SearchIcon,
  MoreVertOutlined as MoreVertIcon,
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  HistoryOutlined as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { Lightning, PaperPlane, ArrowUp } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { Client } from '@langchain/langgraph-sdk';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import { format, parseISO, startOfDay, differenceInDays } from 'date-fns';
import {
  createConversation,
  getCollectionConversations,
  getConversationById,
  deleteConversation,
  updateConversationThreadId
} from '@/services/collectionChatService';
import {
  CollectionConversation,
  CollectionChatMessageChunk
} from '@/types/collectionChatTypes';
import { useViewportHeight } from '@/hooks/useViewportHeight';

const LANGGRAPH_BASE_URL =
  import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';

const MOBILE_BOTTOM_NAV_HEIGHT = 64;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isStreaming?: boolean;
  chunks?: CollectionChatMessageChunk[];
}

const getAuthToken = async (
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error) {
    return await getAccessTokenWithPopup(authParams);
  }
};

const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: {
      Authorization: `Bearer ${token}`,
      'X-User-Id': userId
    }
  });

// Helper functions for generating tool call/result descriptions
const generateToolCallDescription = (
  toolName: string,
  toolArgs: Record<string, any>
): string => {
  const descriptions: Record<string, (args: any) => string> = {
    search_collections: (args) =>
      `🔍 Searching collections for: ${
        args.query?.slice(0, 50) || 'information'
      }...`,
    search_all_summaries: (args) =>
      `📚 Searching all session summaries for: ${
        args.query?.slice(0, 50) || 'information'
      }...`,
    search_all_insights: (args) =>
      `💡 Searching all insights for: ${
        args.query?.slice(0, 50) || 'information'
      }...`,
    get_collection_overview: () => '📂 Fetching collection overview...',
    get_session_details: () => '📄 Fetching session details...',
    get_session_segments: (args) =>
      `📝 Loading segments ${args.offset || 0}-${
        (args.offset || 0) + (args.limit || 0)
      }...`
  };

  const descFn = descriptions[toolName] || (() => `⚙️ Calling ${toolName}...`);
  return descFn(toolArgs);
};

const generateToolResultDescription = (toolName: string): string => {
  const descriptions: Record<string, string> = {
    search_collections: 'Collection search complete',
    search_all_summaries: 'Summary search complete',
    search_all_insights: 'Insight search complete',
    get_collection_overview: 'Collection overview retrieved',
    get_session_details: 'Session details retrieved',
    get_session_segments: 'Segments loaded'
  };

  return descriptions[toolName] || `${toolName} complete`;
};

// Helper to process chunks: pair tool_calls with their results
// Only show the most recent tool_result (replace previous ones)
const processChunksForDisplay = (
  chunks: CollectionChatMessageChunk[]
): CollectionChatMessageChunk[] => {
  if (!chunks || chunks.length === 0) return [];

  // Build a map of tool names to their result chunks (keep only the latest)
  const toolResultMap = new Map<string, CollectionChatMessageChunk>();
  chunks.forEach((chunk) => {
    if (chunk.chunkType === 'tool_result' && chunk.toolName) {
      // Only keep the latest result for each tool
      const existing = toolResultMap.get(chunk.toolName);
      if (!existing || (chunk.sequence || 0) > (existing.sequence || 0)) {
        toolResultMap.set(chunk.toolName, chunk);
      }
    }
  });

  // Find the most recent tool_result overall (to show only one status)
  let latestToolResult: CollectionChatMessageChunk | null = null;
  toolResultMap.forEach((chunk) => {
    if (!latestToolResult || (chunk.sequence || 0) > (latestToolResult.sequence || 0)) {
      latestToolResult = chunk;
    }
  });

  // Filter chunks: skip tool_calls that have a corresponding tool_result
  // Only include the latest tool_result
  const displayChunks: CollectionChatMessageChunk[] = [];
  chunks.forEach((chunk) => {
    if (
      chunk.chunkType === 'tool_call' &&
      chunk.toolName &&
      toolResultMap.has(chunk.toolName)
    ) {
      // Skip this tool_call because we have a result for it
      return;
    }
    // Only include the latest tool_result, skip older ones
    if (chunk.chunkType === 'tool_result') {
      if (latestToolResult && chunk.id === latestToolResult.id) {
        displayChunks.push(chunk);
      }
      return;
    }
    // Include tool_calls without results
    if (chunk.chunkType === 'tool_call') {
      displayChunks.push(chunk);
    }
  });

  return displayChunks;
};

const extractTextFromContent = (content: unknown): string => {
  if (!content) {
    return '';
  }

  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map(extractTextFromContent).join('');
  }

  if (typeof content === 'object') {
    const obj = content as Record<string, unknown>;

    if (typeof obj.text === 'string') {
      return obj.text;
    }

    if (typeof obj.content !== 'undefined') {
      return extractTextFromContent(obj.content);
    }

    if (Array.isArray(obj.parts)) {
      return obj.parts.map(extractTextFromContent).join('');
    }
  }

  return '';
};

// Component to render tool call/result chunks (Cursor-style)
const ToolChunk: React.FC<{
  chunk: CollectionChatMessageChunk;
  theme: any;
}> = ({ chunk, theme }) => {
  const isToolCall = chunk.chunkType === 'tool_call';
  const isToolResult = chunk.chunkType === 'tool_result';

  if (!isToolCall && !isToolResult) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        my: 0.5,
        borderRadius: 1,
        bgcolor: isToolCall
          ? theme.palette.mode === 'dark'
            ? 'rgba(255, 193, 7, 0.1)'
            : 'rgba(255, 193, 7, 0.08)'
          : theme.palette.mode === 'dark'
          ? 'rgba(76, 175, 80, 0.1)'
          : 'rgba(76, 175, 80, 0.08)',
        border: '1px solid',
        borderColor: isToolCall
          ? theme.palette.mode === 'dark'
            ? 'rgba(255, 193, 7, 0.3)'
            : 'rgba(255, 193, 7, 0.2)'
          : theme.palette.mode === 'dark'
          ? 'rgba(76, 175, 80, 0.3)'
          : 'rgba(76, 175, 80, 0.2)',
        fontSize: '0.8rem',
        width: 'fit-content',
        maxWidth: '100%'
      }}
    >
      {isToolCall && (
        <CircularProgress
          size={10}
          thickness={5}
          sx={{ color: theme.palette.warning.main, flexShrink: 0 }}
        />
      )}
      {isToolResult && (
        <CheckCircleIcon
          sx={{
            fontSize: '0.875rem',
            color: theme.palette.success.main,
            flexShrink: 0
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: isToolCall ? 'warning.main' : 'success.main',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {chunk.content}
      </Typography>
    </Box>
  );
};

export const AiChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [conversations, setConversations] = useState<CollectionConversation[]>(
    []
  );
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [historyDrawerOpen] = useState(true);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  // Creating conversation state (matches CollectionMultiChatUI)
  const [justCreatedConversation, setJustCreatedConversation] = useState(false);

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { height: viewportHeight, isKeyboardOpen } = useViewportHeight();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();

  // Set up header
  useEffect(() => {
    setHeaderText('Ask');
    setMenuActions(null);

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [setHeaderText, setMenuActions]);

  // Load global chat conversations on component mount
  useEffect(() => {
    const loadConversations = async () => {
      if (!appUser?.id) return;

      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        // Get all conversations with null sessionCollectionId (global chats)
        const allConversations = await getCollectionConversations(
          null, // null means global conversations
          token,
          appUser.id
        );

        setConversations(allConversations);
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    };

    loadConversations();
  }, [appUser, getAccessTokenSilently, getAccessTokenWithPopup]);

  // Load selected conversation messages
  useEffect(() => {
    console.log(
      '[AiChatPage] Load messages effect triggered - conversationId:',
      selectedConversationId,
      'justCreated:',
      justCreatedConversation
    );

    if (!selectedConversationId || !appUser?.id) {
      console.log('[AiChatPage] Clearing messages - no conversation selected');
      setMessages([]);
      return;
    }

    // Don't reload messages if we just created this conversation and are sending the first message
    if (justCreatedConversation) {
      console.log(
        '[AiChatPage] Skipping message load for just-created conversation'
      );
      return;
    }

    const loadConversationMessages = async () => {
      console.log(
        '[AiChatPage] Loading messages for conversation:',
        selectedConversationId
      );
      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const conversation = await getConversationById(
          selectedConversationId,
          token,
          appUser.id
        );

        if (conversation && conversation.messages) {
          const loadedMessages: ChatMessage[] = conversation.messages.map(
            (interaction) => ({
              id: interaction.id,
              role: interaction.role === 0 ? 'user' : 'assistant',
              content: interaction.content || '',
              timestamp: interaction.timestamp,
              chunks: interaction.chunks || []
            })
          );
          console.log('[AiChatPage] Loaded', loadedMessages.length, 'messages');
          setMessages(loadedMessages);
        }
      } catch (error) {
        console.error('Failed to load conversation messages:', error);
      }
    };

    loadConversationMessages();
    // Note: justCreatedConversation is NOT in the dependency array
    // We only want this to run when the conversation ID changes or on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId, appUser?.id]);

  // Handle initial prompt from FAB
  useEffect(() => {
    if (location.state?.initialPrompt) {
      setInputValue(location.state.initialPrompt);
      // Clear the state to prevent re-triggering
      navigate('/ai-chat', { replace: true });
    }
  }, [location.state, navigate]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-focus chat input on page load (skip on mobile to avoid keyboard popup)
  useEffect(() => {
    if (!isMobile && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isMobile]);

  // Auto-focus chat input when there are messages and not loading (skip on mobile)
  useEffect(() => {
    if (!isMobile && messages.length > 0 && chatInputRef.current && !isSending) {
      chatInputRef.current.focus();
    }
  }, [messages.length, isSending, isMobile]);

  // Create new global conversation
  const createNewConversation =
    useCallback(async (): Promise<CollectionConversation> => {
      if (!appUser?.id) throw new Error('No appUser');

      setJustCreatedConversation(true);

      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Create conversation with null sessionCollectionId (global chat)
      const conversation = await createConversation(
        {
          title: 'New Global Chat',
          sessionCollectionId: null // null for global chats
        },
        token,
        appUser.id
      );

      setConversations((prev) => [conversation, ...prev]);
      setSelectedConversationId(conversation.id);

      return conversation;
    }, [appUser, getAccessTokenSilently, getAccessTokenWithPopup]);

  // Handle creating new conversation from header button
  const handleNewChat = useCallback(async () => {
    try {
      const newConv = await createNewConversation();
      setMessages([]);
      setInputValue('');
      // Focus the input after state updates
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 0);
    } catch (error) {
      console.error('Failed to create new conversation:', error);
    }
  }, [createNewConversation]);

  // Update header with button
  useEffect(() => {
    setMenuActions(
      isMobile ? (
        <IconButton
          onClick={handleNewChat}
          sx={{
            backgroundColor:
              theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewChat}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor:
              theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          New Chat
        </Button>
      )
    );
  }, [handleNewChat, theme.palette.mode, setMenuActions, isMobile]);

  // Send message with LangGraph global_chat_agent
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isSending || !appUser?.id) return;

    const content = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    // If no conversation selected, create one first
    let conversationId = selectedConversationId;
    let newlyCreatedConversation: CollectionConversation | null = null;

    if (!conversationId) {
      try {
        console.log('[AiChatPage] No conversation selected, creating new one');
        newlyCreatedConversation = await createNewConversation();
        conversationId = newlyCreatedConversation.id;
        console.log(
          '[AiChatPage] Created new global conversation:',
          conversationId
        );
      } catch (error) {
        console.error('[AiChatPage] Failed to create conversation:', error);
        setInputValue(content); // Restore input
        setIsSending(false);
        return;
      }
    }

    const assistantMessageId = uuidv4();

    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      abortControllerRef.current = new AbortController();
      const client = createLangGraphClient(
        LANGGRAPH_BASE_URL,
        token,
        appUser.id
      );

      // Check if this is the first message in the conversation
      const isFirstMessage = messages.length === 0;

      // Add user message
      const userMessage: ChatMessage = {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date().toISOString()
      };
      console.log('[AiChatPage] Adding user message to UI');
      setMessages((prev) => {
        console.log('[AiChatPage] Messages before user message:', prev.length);
        return [...prev, userMessage];
      });

      // Add streaming assistant message
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
        chunks: [] // Initialize chunks array to ensure React tracks changes
      };
      console.log('[AiChatPage] Adding assistant message to UI');
      setMessages((prev) => {
        console.log(
          '[AiChatPage] Messages before assistant message:',
          prev.length
        );
        return [...prev, assistantMessage];
      });

      // Get or create thread ID for this conversation
      const selectedConv =
        newlyCreatedConversation ||
        conversations.find((c) => c.id === conversationId);
      let threadId = selectedConv?.threadId;

      console.log(
        'Sending message to conversation:',
        conversationId,
        'threadId:',
        threadId
      );

      if (!threadId) {
        const createdThread = await client.threads.create();
        threadId = createdThread.thread_id;

        // Save thread ID to database
        try {
          await updateConversationThreadId(
            conversationId,
            threadId,
            token,
            appUser.id
          );
          console.log(
            '[AiChatPage] Saved thread ID to conversation:',
            threadId
          );

          // Update local conversation state
          setConversations((prev) =>
            prev.map((c) => (c.id === conversationId ? { ...c, threadId } : c))
          );
        } catch (error) {
          console.error(
            '[AiChatPage] Failed to save thread ID to database:',
            error
          );
          // Continue anyway - the thread will still work, just won't be persisted
        }
      }

      // If this is the first message, generate a conversation title
      if (isFirstMessage) {
        // Run title generation in parallel (don't block the chat)
        (async () => {
          try {
            // Create a separate thread for title generation
            const titleThread = await client.threads.create();
            const titleThreadId = titleThread.thread_id;

            console.log('[AiChatPage] Generating title...');
            const titleResult = await client.runs.wait(
              titleThreadId,
              'title_generation_graph',
              {
                input: {
                  user_message: content,
                  conversation_id: conversationId,
                  auth_token: token,
                  user_id: appUser.id
                }
              }
            );

            // Extract title from the result (handle different possible response structures)
            const title =
              (titleResult as any)?.title || (titleResult as any)?.data?.title;

            if (title) {
              console.log('[AiChatPage] Title generated:', title);
              // Update the conversation title in local state
              // No need to refetch - the agent already updated the database
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === conversationId ? { ...c, title: title } : c
                )
              );
            }
          } catch (titleError) {
            console.error('[AiChatPage] Failed to generate title:', titleError);
            // Continue with chat even if title generation fails
          }
        })();
      }

      // Stream from the global_chat_agent
      const stream = client.runs.stream(threadId, 'global_chat_agent', {
        input: {
          messages: [{ type: 'human', content }]
        },
        config: {
          configurable: {
            auth_token: token,
            bearer_token: token,
            conversation_id: conversationId,
            user_id: appUser.id,
            company_id: selectedUserRole?.companyId
          }
        },
        metadata: {
          conversation_id: conversationId,
          thread_id: threadId,
          user_id: appUser.id
        },
        streamMode: 'messages-tuple',
        streamSubgraphs: true,
        signal: abortControllerRef.current.signal
      });

      let accumulatedContent = '';
      let chunkSequence = 0;
      const streamingChunks: CollectionChatMessageChunk[] = [];
      const processedToolCalls = new Set<string>();

      for await (const chunk of stream as any) {
        console.log('[AiChatPage] Stream chunk received:', chunk);

        // Handle messages-tuple mode: data = [message, metadata]
        if (chunk.event === 'messages') {
          const data = chunk.data;
          console.log('[AiChatPage] Messages event - data:', data);

          if (Array.isArray(data) && data.length === 2) {
            const [msg, meta] = data as [any, any];

            // Skip summarization middleware chunks
            if (meta?.langgraph_node?.startsWith('SummarizationMiddleware')) {
              console.log(
                '[AiChatPage] Skipping summarization middleware chunk'
              );
              continue;
            }

            // Handle tool calls
            if (msg?.tool_calls && Array.isArray(msg.tool_calls)) {
              console.log(
                '[AiChatPage] Tool calls detected:',
                msg.tool_calls.length
              );
              for (const toolCall of msg.tool_calls) {
                const toolId = toolCall.id || '';
                if (toolId && !processedToolCalls.has(toolId)) {
                  processedToolCalls.add(toolId);
                  chunkSequence++;

                  const toolCallChunk: CollectionChatMessageChunk = {
                    id: `chunk-${chunkSequence}`,
                    sequence: chunkSequence,
                    chunkType: 'tool_call',
                    content: generateToolCallDescription(
                      toolCall.name,
                      toolCall.args || {}
                    ),
                    toolName: toolCall.name,
                    toolArgs: JSON.stringify(toolCall.args || {}),
                    toolResult: null,
                    timestamp: new Date().toISOString()
                  };

                  streamingChunks.push(toolCallChunk);
                  console.log(
                    '[AiChatPage] Added tool call chunk:',
                    toolCallChunk.content
                  );

                  setMessages((prev) => {
                    const updated = [...prev];
                    const assistantMsgIdx = updated.findIndex(
                      (m) =>
                        m.id === assistantMessageId && m.role === 'assistant'
                    );
                    if (assistantMsgIdx >= 0) {
                      updated[assistantMsgIdx] = {
                        ...updated[assistantMsgIdx],
                        chunks: [...streamingChunks],
                        isStreaming: true
                      };
                    }
                    return updated;
                  });
                }
              }
            }

            // Handle tool results
            if (msg?.type === 'tool' && msg?.name) {
              chunkSequence++;
              console.log('[AiChatPage] Tool result detected:', msg.name);

              const toolResultChunk: CollectionChatMessageChunk = {
                id: `chunk-${chunkSequence}`,
                sequence: chunkSequence,
                chunkType: 'tool_result',
                content: generateToolResultDescription(msg.name),
                toolName: msg.name,
                toolArgs: null,
                toolResult:
                  typeof msg.content === 'string'
                    ? msg.content
                    : JSON.stringify(msg.content),
                timestamp: new Date().toISOString()
              };

              streamingChunks.push(toolResultChunk);
              console.log(
                '[AiChatPage] Added tool result chunk:',
                toolResultChunk.content
              );

              setMessages((prev) => {
                const updated = [...prev];
                const assistantMsgIdx = updated.findIndex(
                  (m) => m.id === assistantMessageId && m.role === 'assistant'
                );
                if (assistantMsgIdx >= 0) {
                  updated[assistantMsgIdx] = {
                    ...updated[assistantMsgIdx],
                    chunks: [...streamingChunks],
                    isStreaming: true
                  };
                }
                return updated;
              });
            }

            // Process AI message content
            if (msg?.type === 'AIMessageChunk') {
              const incrementalText = extractTextFromContent(msg.content);
              if (incrementalText) {
                accumulatedContent += incrementalText;

                const lastChunk = streamingChunks[streamingChunks.length - 1];
                if (lastChunk && lastChunk.chunkType === 'text') {
                  lastChunk.content += incrementalText;
                } else {
                  chunkSequence++;
                  streamingChunks.push({
                    id: `chunk-${chunkSequence}`,
                    sequence: chunkSequence,
                    chunkType: 'text',
                    content: incrementalText,
                    toolName: null,
                    toolArgs: null,
                    toolResult: null,
                    timestamp: new Date().toISOString()
                  });
                }

                setMessages((prev) => {
                  const updated = [...prev];
                  const assistantMsgIdx = updated.findIndex(
                    (m) => m.id === assistantMessageId && m.role === 'assistant'
                  );
                  if (assistantMsgIdx >= 0) {
                    updated[assistantMsgIdx] = {
                      ...updated[assistantMsgIdx],
                      content: accumulatedContent,
                      chunks: [...streamingChunks],
                      isStreaming: true
                    };
                  }
                  return updated;
                });
              }
            } else if (msg?.type === 'ai' || msg?.role === 'assistant') {
              const finalText = extractTextFromContent(msg.content);
              if (finalText) {
                let incrementalText = '';

                if (finalText.length > accumulatedContent.length) {
                  incrementalText = finalText.slice(accumulatedContent.length);
                } else if (!accumulatedContent) {
                  incrementalText = finalText;
                }

                if (incrementalText) {
                  const lastChunk = streamingChunks[streamingChunks.length - 1];
                  if (lastChunk && lastChunk.chunkType === 'text') {
                    lastChunk.content += incrementalText;
                  } else {
                    chunkSequence++;
                    streamingChunks.push({
                      id: `chunk-${chunkSequence}`,
                      sequence: chunkSequence,
                      chunkType: 'text',
                      content: incrementalText,
                      toolName: null,
                      toolArgs: null,
                      toolResult: null,
                      timestamp: new Date().toISOString()
                    });
                  }
                }

                accumulatedContent = finalText;

                setMessages((prev) => {
                  const updated = [...prev];
                  const assistantMsgIdx = updated.findIndex(
                    (m) => m.id === assistantMessageId && m.role === 'assistant'
                  );
                  if (assistantMsgIdx >= 0) {
                    updated[assistantMsgIdx] = {
                      ...updated[assistantMsgIdx],
                      content: accumulatedContent,
                      chunks: [...streamingChunks],
                      isStreaming: true
                    };
                  }
                  return updated;
                });
              }
            }
          }
        }
      }

      console.log(
        '[AiChatPage] Stream complete. Total chunks:',
        streamingChunks.length
      );

      // Finalize the streaming message
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsgIdx = updated.findIndex(
          (m) => m.id === assistantMessageId && m.role === 'assistant'
        );
        if (assistantMsgIdx >= 0) {
          updated[assistantMsgIdx] = {
            ...updated[assistantMsgIdx],
            isStreaming: false
          };
        }
        return updated;
      });

      // Update conversation list only if we didn't just create it
      // (For new conversations, it's already in the list; for existing ones, update message count)
      console.log(
        '[AiChatPage] Stream complete. newlyCreatedConversation:',
        !!newlyCreatedConversation
      );
      if (!newlyCreatedConversation) {
        console.log('[AiChatPage] Refreshing conversation list');
        const updatedConversations = await getCollectionConversations(
          null,
          token,
          appUser.id
        );
        setConversations(updatedConversations);
      } else {
        console.log(
          '[AiChatPage] Skipping conversation list refresh (just created)'
        );
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Stream aborted by user');
      } else {
        console.error('Error sending message:', error);
        setMessages((prev) => {
          const updated = [...prev];
          const assistantMsgIdx = updated.findIndex(
            (m) => m.id === assistantMessageId && m.role === 'assistant'
          );
          if (assistantMsgIdx >= 0) {
            updated[assistantMsgIdx] = {
              ...updated[assistantMsgIdx],
              content: 'Sorry, I encountered an error. Please try again.',
              isStreaming: false
            };
          }
          return updated;
        });
      }
    } finally {
      setIsSending(false);
      setJustCreatedConversation(false);
      abortControllerRef.current = null;
    }
  }, [
    inputValue,
    isSending,
    appUser,
    selectedConversationId,
    conversations,
    createNewConversation,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSelectedConversationId(null);
    if (isMobile) {
      setMobileHistoryOpen(false);
    }
  };

  const loadChatSession = (conversation: CollectionConversation) => {
    setSelectedConversationId(conversation.id);
    if (isMobile) {
      setMobileHistoryOpen(false);
    }
  };

  const handleSearchToggle = () => {
    const newExpanded = !searchExpanded;
    setSearchExpanded(newExpanded);
    if (newExpanded) {
      // Focus the search input after it's rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      setSearchQuery('');
    }
  };

  const filteredConversations = conversations.filter(
    (session) =>
      searchQuery === '' ||
      session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDateCategory = (dateStr: string): { key: string; label: string; sortOrder: number } => {
    const date = parseISO(dateStr);
    const now = new Date();
    const today = startOfDay(now);
    const dateStart = startOfDay(date);
    const diffInDays = differenceInDays(today, dateStart);

    if (diffInDays === 0) {
      return { key: 'today', label: 'Today', sortOrder: 0 };
    } else if (diffInDays === 1) {
      return { key: 'yesterday', label: 'Yesterday', sortOrder: 1 };
    } else if (diffInDays <= 7) {
      return { key: 'lastWeek', label: 'Last Week', sortOrder: 2 };
    } else if (diffInDays <= 30) {
      return { key: 'lastMonth', label: 'Last Month', sortOrder: 3 };
    } else if (diffInDays <= 365) {
      return { key: 'lastYear', label: 'Last Year', sortOrder: 4 };
    } else {
      return { key: 'older', label: 'Older', sortOrder: 5 };
    }
  };

  // Group conversations by date category
  const groupedConversations = filteredConversations.reduce((groups, session) => {
    const dateCategory = getDateCategory(session.lastMessageAt || session.createdAt);
    if (!groups[dateCategory.key]) {
      groups[dateCategory.key] = {
        label: dateCategory.label,
        sortOrder: dateCategory.sortOrder,
        sessions: []
      };
    }
    groups[dateCategory.key].sessions.push(session);
    return groups;
  }, {} as Record<string, { label: string; sortOrder: number; sessions: typeof filteredConversations }>);

  // Sort groups by sortOrder and sessions within each group by date (newest first)
  const sortedGroups = Object.values(groupedConversations)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(group => ({
      ...group,
      sessions: group.sessions.sort((a, b) => {
        const dateA = parseISO(a.lastMessageAt || a.createdAt);
        const dateB = parseISO(b.lastMessageAt || b.createdAt);
        return dateB.getTime() - dateA.getTime();
      })
    }));

  const handleContextMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    sessionId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setSelectedSessionId(sessionId);
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(null);
    setSelectedSessionId(null);
    setIsRenaming(false);
    setRenameValue('');
  };

  const handleDeleteChat = async () => {
    if (selectedSessionId && appUser?.id) {
      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        await deleteConversation(selectedSessionId, token, appUser.id);
        setConversations((prev) =>
          prev.filter((s) => s.id !== selectedSessionId)
        );

        // If we're deleting the current session, clear the messages
        if (selectedSessionId === selectedConversationId) {
          setMessages([]);
          setSelectedConversationId(null);
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
    }
    handleContextMenuClose();
  };

  const handleRenameChat = () => {
    const session = conversations.find((s) => s.id === selectedSessionId);
    if (session) {
      setRenameValue(session.title);
      setIsRenaming(true);
    }
  };

  const handleRenameSubmit = async () => {
    if (selectedSessionId && renameValue.trim() && appUser?.id) {
      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        // TODO: Add update conversation endpoint to backend
        // For now, just update locally
        setConversations((prev) =>
          prev.map((s) =>
            s.id === selectedSessionId ? { ...s, title: renameValue.trim() } : s
          )
        );
      } catch (error) {
        console.error('Failed to rename conversation:', error);
      }
    }
    handleContextMenuClose();
  };

  const handleRenameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleRenameSubmit();
    } else if (event.key === 'Escape') {
      handleContextMenuClose();
    }
  };

  const handleCopyMessage = async (message: ChatMessage) => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopiedMessageId(message.id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const historyDrawerContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2
            }}
          >
            <Typography
              sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}
            >
              History
            </Typography>
            {isMobile && (
              <IconButton
                onClick={() => setMobileHistoryOpen(false)}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          {/* Search and New Chat Row */}
          <Box sx={{ position: 'relative' }}>
            {searchExpanded && (
              <TextField
                inputRef={searchInputRef}
                fullWidth
                size="small"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                autoFocus
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 10,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: theme.palette.background.paper,
                    fontSize: '13px'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleSearchToggle}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={handleNewChat}
                sx={{
                  borderRadius: '6px',
                  flex: 1,
                  opacity: searchExpanded ? 0 : 1,
                  transition: 'opacity 0.2s ease',
                  fontSize: '13px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: theme.palette.divider,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f9fafb'
                  }
                }}
              >
                New Chat
              </Button>

              <IconButton
                onClick={handleSearchToggle}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  width: 36,
                  height: 36,
                  opacity: searchExpanded ? 0 : 1,
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f9fafb'
                  }
                }}
              >
                <SearchIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Chat Sessions List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredConversations.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No chats found' : 'No history yet'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1 }}>
            {sortedGroups.map((group) => (
              <Box key={group.label} sx={{ mb: 1.5 }}>
                {/* Date Header */}
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    pl: 1.5,
                    pt: 0.5,
                    pb: 0.25,
                    mb: 0.25,
                    textAlign: 'left'
                  }}
                >
                  {group.label}
                </Typography>
                {/* Conversations in this group */}
                <List sx={{ py: 0 }}>
                  {group.sessions.map((session) => (
                    <ListItemButton
                      key={session.id}
                      onClick={() => loadChatSession(session)}
                      selected={selectedConversationId === session.id}
                      sx={{
                        borderRadius: 1,
                        mb: 0.25,
                        pr: 1,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main + '12'
                        },
                        '&:hover': {
                          '& .chat-menu-button': {
                            opacity: 1
                          }
                        }
                      }}
                    >
                      <ListItemText
                        primary={session.title}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          noWrap: true,
                          color: 'text.secondary'
                        }}
                      />
                      <IconButton
                        className="chat-menu-button"
                        size="small"
                        onClick={(e) => handleContextMenuOpen(e, session.id)}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          ml: 1,
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        // Use dynamic viewport height on mobile for proper keyboard handling
        height: isMobile 
          ? `${viewportHeight - 56}px`
          : 'calc(100vh - 64px)',
        overflow: 'hidden',
        width: '100%',
        bgcolor: 'background.default',
        transition: isMobile ? 'height 0.1s ease-out' : 'none'
      }}
    >
      {/* History Drawer - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            width: historyDrawerOpen ? 280 : 0,
            flexShrink: 0,
            transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
              width: historyDrawerOpen ? 280 : 0,
              boxSizing: 'border-box',
              position: 'relative',
              borderRight: `1px solid ${theme.palette.divider}`,
              transition: 'width 0.3s ease',
              overflow: 'hidden'
            }
          }}
        >
          {historyDrawerOpen && historyDrawerContent}
        </Drawer>
      )}

      {/* History Drawer - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileHistoryOpen}
          onClose={() => setMobileHistoryOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: '75%',
              maxWidth: 360,
              boxSizing: 'border-box'
            }
          }}
        >
          {historyDrawerContent}
        </Drawer>
      )}

      {/* Main Chat Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Mobile Header Bar - fixed at top of chat area */}
        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              flexShrink: 0,
              minHeight: 48,
              zIndex: 10
            }}
          >
            {/* Left: History Button */}
            <IconButton
              onClick={() => setMobileHistoryOpen(true)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <HistoryIcon sx={{ fontSize: 20, color: 'text.primary' }} />
            </IconButton>

            {/* Center: Current Chat Title or "New Chat" */}
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: theme.palette.text.primary,
                flex: 1,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 1
              }}
            >
              {selectedConversationId 
                ? (conversations.find(c => c.id === selectedConversationId)?.title || 'Chat')
                : 'New Chat'}
            </Typography>

            {/* Spacer to balance the history button on the left */}
            <Box sx={{ width: 36, height: 36 }} />
          </Box>
        )}

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {messages.length === 0 ? (
            // Welcome Screen - position towards top on mobile to leave room for input near keyboard
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                pt: { xs: 8, sm: 0 },
                height: '100%',
                width: '100%',
                gap: 2,
                color: 'text.secondary'
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '6px',
                  background:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : '#f3f4f6',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.text.secondary
                }}
              >
                <Lightning size={24} weight="light" />
              </Box>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 0.5
                }}
              >
                How can I help you today?
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  maxWidth: 420,
                  lineHeight: 1.5
                }}
              >
                Search across all your sessions and collections to find insights, patterns, or specific information.
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  mt: 1,
                  opacity: 0.7,
                  fontStyle: 'italic'
                }}
              >
                💡 Tip: To ask about a specific file, use the chat panel on that file's page
              </Typography>
            </Box>
          ) : (
            messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  width: '100%',
                  mb: message.role === 'assistant' ? 2.5 : 1.5
                }}
              >
                {message.role === 'user' ? (
                  // User message: small dialog box, right-aligned
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%'
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: '75%',
                        p: 1.25,
                        px: 1.5,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.08)'
                            : '#f3f4f6',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        position: 'relative'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          lineHeight: 1.5,
                          color: theme.palette.text.primary,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  // Assistant message: plain text below
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5
                    }}
                  >
                    {/* Tool Chunks - always show if present */}
                    {message.chunks && message.chunks.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          mb: message.content ? 1 : 0
                        }}
                      >
                        {processChunksForDisplay(message.chunks).map(
                          (chunk) => (
                            <ToolChunk
                              key={chunk.id}
                              chunk={chunk}
                              theme={theme}
                            />
                          )
                        )}
                      </Box>
                    )}

                    {/* Loading indicator - show only when streaming with no content and no chunks */}
                    {message.isStreaming &&
                      !message.content &&
                      (!message.chunks || message.chunks.length === 0) && (
                        <Box sx={{ pl: 0 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              py: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                animation: 'typing-dot 1.4s infinite',
                                animationDelay: '0s',
                                '@keyframes typing-dot': {
                                  '0%, 60%, 100%': { opacity: 0.3 },
                                  '30%': { opacity: 1 }
                                }
                              }}
                            />
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                animation: 'typing-dot 1.4s infinite',
                                animationDelay: '0.2s',
                                '@keyframes typing-dot': {
                                  '0%, 60%, 100%': { opacity: 0.3 },
                                  '30%': { opacity: 1 }
                                }
                              }}
                            />
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                animation: 'typing-dot 1.4s infinite',
                                animationDelay: '0.4s',
                                '@keyframes typing-dot': {
                                  '0%, 60%, 100%': { opacity: 0.3 },
                                  '30%': { opacity: 1 }
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                    {/* Text Content */}
                    {message.content && (
                      <Box>
                        <Box
                          sx={{
                            fontSize: '14px',
                            lineHeight: 1.7,
                            color: theme.palette.text.primary,
                            textAlign: 'left',
                            '& p': { m: 0, textAlign: 'left', mb: 1.5 },
                            '& p:last-child': { mb: 0 },
                            '& ul, & ol': { my: 1.5, pl: 2.5, textAlign: 'left' },
                            '& li': { my: 0.75, textAlign: 'left' },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              mt: 2,
                              mb: 1,
                              textAlign: 'left',
                              fontWeight: 600
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& code': {
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.1)'
                                  : 'rgba(0,0,0,0.06)',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.375,
                              fontSize: '0.9em',
                              fontFamily: 'monospace'
                            },
                            '& pre': {
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.05)'
                                  : 'rgba(0,0,0,0.03)',
                              p: 1.5,
                              borderRadius: 0.5,
                              overflow: 'auto',
                              my: 1.5,
                              '& code': {
                                bgcolor: 'transparent',
                                p: 0
                              }
                            },
                            '& blockquote': {
                              borderLeft: `3px solid ${theme.palette.divider}`,
                              pl: 1.5,
                              my: 1.5,
                              fontStyle: 'italic',
                              color: theme.palette.text.secondary
                            },
                            '& a': {
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }
                          }}
                        >
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </Box>
                        {/* Copy Button */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            mt: 1
                          }}
                        >
                          <Tooltip title={copiedMessageId === message.id ? 'Copied!' : 'Copy'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyMessage(message)}
                              sx={{
                                color: copiedMessageId === message.id 
                                  ? theme.palette.success.main 
                                  : theme.palette.text.secondary,
                                width: isMobile ? 24 : 28,
                                height: isMobile ? 24 : 28,
                                padding: isMobile ? '3px' : '4px',
                                borderRadius: '6px',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              <CopyIcon sx={{ fontSize: isMobile ? 12 : 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            pb: {
              // When keyboard is open, reduce bottom padding since nav is covered
              xs: isKeyboardOpen 
                ? 2 
                : `calc(env(safe-area-inset-bottom) + ${MOBILE_BOTTOM_NAV_HEIGHT + 8}px)`,
              sm: 2
            },
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            flexShrink: 0
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
              p: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : '#fafafa',
              transition: 'all 0.2s ease',
              '&:focus-within': {
                borderColor: '#6b7280',
                backgroundColor: theme.palette.background.paper
              }
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask across all files and folders"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                // On mobile, scroll input into view after keyboard opens
                if (isMobile) {
                  setTimeout(() => {
                    chatInputRef.current?.scrollIntoView({ 
                      behavior: 'smooth', 
                      block: 'center' 
                    });
                  }, 300);
                }
              }}
              disabled={isSending}
              inputRef={chatInputRef}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '14px',
                  lineHeight: 1.5,
                  '& textarea': {
                    '&::placeholder': {
                      color: theme.palette.text.secondary,
                      opacity: 0.7
                    }
                  }
                }
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={isSending || !inputValue.trim()}
              size="small"
              sx={{
                width: 28,
                height: 28,
                bgcolor:
                  isSending || !inputValue.trim()
                    ? theme.palette.action.disabledBackground
                    : '#1a1a1a',
                color:
                  isSending || !inputValue.trim()
                    ? theme.palette.action.disabled
                    : 'white',
                borderRadius: '6px',
                flexShrink: 0,
                '&:hover': {
                  bgcolor:
                    isSending || !inputValue.trim()
                      ? theme.palette.action.disabledBackground
                      : '#000'
                },
                '&:disabled': {
                  bgcolor: theme.palette.action.disabledBackground
                }
              }}
            >
              {isSending ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <ArrowUp size={16} weight="bold" />
              )}
            </IconButton>
          </Box>

          {/* Keyboard hint */}
          <Typography
            sx={{
              fontSize: '11px',
              color: theme.palette.text.secondary,
              mt: 1,
              textAlign: 'left',
              opacity: 0.7,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Press Enter to send, Shift+Enter for new line
          </Typography>
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleContextMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {isRenaming ? (
          <Box sx={{ p: 2.5, minWidth: 240 }}>
            <TextField
              fullWidth
              size="small"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyPress={handleRenameKeyPress}
              placeholder="Enter new name"
              autoFocus
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  fontSize: '13px',
                  borderRadius: '6px'
                }
              }}
            />
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end'
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={handleContextMenuClose}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : '#d1d5db'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleRenameSubmit}
                variant="contained"
                disabled={!renameValue.trim()}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 0.75,
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                  color: 'white',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          [
            <MenuItem key="rename" onClick={handleRenameChat}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Rename" />
            </MenuItem>,
            <MenuItem
              key="delete"
              onClick={handleDeleteChat}
              sx={{ color: theme.palette.error.main }}
            >
              <ListItemIcon>
                <DeleteIcon
                  fontSize="small"
                  sx={{ color: theme.palette.error.main }}
                />
              </ListItemIcon>
              <ListItemText primary="Delete" />
            </MenuItem>
          ]
        )}
      </Menu>
    </Box>
  );
};

export default AiChatPage;
